/** Peças do painel. As classes vivem em design.css; aqui só a semântica e os estados. */

import type { CSSProperties, ReactNode } from "react";
import { Icon, type IconName } from "./Icon";
import type { HealthLevel, ZoneColor } from "../lib/types";

const ZONE_CLASS: Record<ZoneColor, string> = {
  rose: "crit",
  orange: "urg",
  amber: "att",
  emerald: "ok",
  indigo: "abn",
  slate: "att",
};
const ZONE_LABEL: Record<ZoneColor, string> = {
  rose: "Crítico",
  orange: "Urgente",
  amber: "Atenção",
  emerald: "Saudável",
  indigo: "Abundante",
  slate: "—",
};

export const zoneClass = (c: ZoneColor) => ZONE_CLASS[c] ?? "att";

/** A zona de um VALOR, pelas faixas do usuário. Espelha `health_color` do motor
 *  (`api/app/engine/projection.py`) para o front poder pintar um número que o servidor
 *  ainda não avaliou — o "depois" de um lançamento que nem foi feito. */
export function zonaDe(valor: number, levels?: HealthLevel[]): string {
  if (!levels?.length) return valor < 0 ? "crit" : valor < 1000 ? "urg" : valor < 3000 ? "att" : valor < 10000 ? "ok" : "abn";
  const achada = [...levels]
    .sort((a, b) => (a.min ?? -Infinity) - (b.min ?? -Infinity))
    .find((l) => valor >= (l.min ?? -Infinity) && valor < (l.max ?? Infinity));
  return achada ? zoneClass(achada.color as ZoneColor) : "att";
}
export const zoneLabel = (c: ZoneColor) => ZONE_LABEL[c] ?? "—";

export function Zone({
  color,
  levels,
  value,
}: {
  color: ZoneColor;
  /** Com as faixas e o valor, o rótulo é o NOME QUE O USUÁRIO DEU à faixa. Sem eles, cai
   *  no nome da cor. Renomear "Saudável" para "Tranquilo" e continuar lendo "Abundante" —
   *  porque o badge lia a cor — fazia a tela falar de uma faixa que não existe mais. */
  levels?: HealthLevel[];
  value?: number;
}) {
  const daFaixa =
    levels?.length && value !== undefined
      ? [...levels]
          .sort((a, b) => (a.min ?? -Infinity) - (b.min ?? -Infinity))
          .find((l) => value >= (l.min ?? -Infinity) && value < (l.max ?? Infinity))?.label
      : undefined;
  return <span className={`zone ${zoneClass(color)}`}>{daFaixa || zoneLabel(color)}</span>;
}

export function Face({
  children,
  className = "",
  paper = false,
  zone,
}: {
  children: ReactNode;
  className?: string;
  paper?: boolean;
  /** Pinta a face inteira com a zona de saúde. A cor é a primeira leitura: você sabe
   *  que o mês aperta antes de ler o número. */
  zone?: ZoneColor;
}) {
  return (
    <div className={`face ${paper ? "paper" : ""} ${zone ? `z-${zoneClass(zone)}` : ""} ${className}`}>
      {children}
    </div>
  );
}

export function Label({ children, dim = false }: { children: ReactNode; dim?: boolean }) {
  return <span className={`lbl ${dim ? "dim" : ""}`}>{children}</span>;
}

/** Campo de formulário: o rótulo envolve o controle, então a associação é implícita
 * (sem id, sem `htmlFor` para esquecer). Um campo, um controle. */
export function Field({
  label,
  hint,
  children,
  className = "",
}: {
  label: string;
  hint?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={`field ${className}`}>
      <span className="lbl">{label}</span>
      {children}
      {hint && <span className="rule">{hint}</span>}
    </label>
  );
}

/** Conjunto de controles sob um rótulo (chips, seletor segmentado): não é um campo,
 * é um grupo, e o nome acessível vai no grupo. */
export function FieldGroup({
  label,
  children,
  className = "",
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`field ${className}`} role="group" aria-label={label}>
      <span className="lbl">{label}</span>
      {children}
    </div>
  );
}

type KeyProps = {
  children?: ReactNode;
  icon?: IconName;
  onClick?: () => void;
  primary?: boolean;
  danger?: boolean;
  wide?: boolean;
  pressed?: boolean;
  disabled?: boolean;
  title?: string;
  type?: "button" | "submit";
  className?: string;
  /** Só para a altura do alvo: a tecla do sistema para em 32px e as telas de ajuste
   *  precisam de 44px no dedo. A cor e a forma continuam vindo do design.css. */
  style?: CSSProperties;
};

/** Tecla do painel: gravada em repouso, afundada quando pressionada. */
export function Key({
  children,
  icon,
  onClick,
  primary,
  danger,
  wide,
  pressed,
  disabled,
  title,
  type = "button",
  className = "",
  style,
}: KeyProps) {
  const iconOnly = icon && !children;
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={style}
      aria-label={iconOnly ? title : undefined}
      aria-pressed={pressed}
      className={`key ${primary ? "primary" : ""} ${danger ? "danger" : ""} ${
        iconOnly ? "icon" : ""
      } ${wide ? "wide" : ""} ${className}`}
    >
      {icon && <Icon name={icon} />}
      {children}
    </button>
  );
}

export function Seg<T extends string>({
  options,
  value,
  onChange,
  small,
  label,
}: {
  options: { id: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
  small?: boolean;
  label: string;
}) {
  return (
    <div className={`seg ${small ? "small" : ""}`} role="group" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          aria-pressed={value === o.id}
          onClick={() => onChange(o.id)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Switch({
  checked,
  onChange,
  children,
  tone,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  children: ReactNode;
  tone?: "sim";
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`switch ${tone === "sim" ? "sim" : ""}`}
    >
      <span className="tr" />
      {children}
    </button>
  );
}

export function Stamp({ text, cls }: { text: string; cls: string }) {
  return <span className={`stamp ${cls}`}>{text}</span>;
}

export function Gauge({ pct, ticks = false }: { pct: number; ticks?: boolean }) {
  return (
    <div className={`gauge ${ticks ? "ticks" : ""}`}>
      <i style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
    </div>
  );
}

/** Placa de identificação da conta: a cor é dado, não decoração. */
export function AccountTag({ name, color }: { name: string; color?: string | null }) {
  return (
    <span className="tag acc">
      <i style={{ background: color ?? "var(--muted)" }} />
      {name}
    </span>
  );
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <b>{title}</b>
      {children && <span>{children}</span>}
    </div>
  );
}

export function Skeleton({ lines = 3 }: { lines?: number }) {
  return (
    <div style={{ display: "grid", gap: 8, padding: "14px 16px" }} aria-busy="true" aria-live="polite">
      {Array.from({ length: lines }).map((_, i) => (
        <div key={i} className="skeleton" style={{ height: i === 0 ? 30 : 16, width: i === 0 ? "60%" : "100%" }}>
          carregando
        </div>
      ))}
    </div>
  );
}

export function ErrorBanner({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="banner" role="alert">
      <span>{message}</span>
      {onRetry && <Key onClick={onRetry}>Tentar de novo</Key>}
    </div>
  );
}
